import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import moment from 'moment';

import {
  Icon, Button, Table, Popover, Modal,
} from '@anna/anna-ws-ui';
import { Tooltip } from 'antd';

import CategoryCell from './components/CategoryCell';
import Image from '../Image';

import {
  getMoreTransactionsActions,
  updateDates,
} from '../../services/transactionsService/actions';

import {
  updateTransactionCategoryActions,
} from '../../services/categoryService/actions';

import styles from './styles.module.css';
import { getChangeRequest, sendChangeRequest } from '../../services/changeRequestsService/actions';
import amountFormatter from '../../utils/amountFormatter';
import { gaSendUserEvent, gaEventName } from '../../utils/analytics';

class TransactionsTable extends Component {
  paymentTypes = {
    bank_transfer: 'Bank transfer',
    contactless: 'Contactless',
    atm: 'ATM',
    card_income: 'Card income',
    card_payment: 'Card payment',
    direct_debit: 'Direct debit',
  };

  manualTitleTypes = {
    BANK: 'Other account',
    CASH: 'Cash',
    OTHER: 'Other person',
  };

  constructor(props) {
    super(props);

    this.state = {
      showImagePopup: false,
      showErrorPopup: false,
      currentTransaction: null,
    };
  }

  componentDidMount() {
    const { actions } = this.props;
    actions.getMoreTransactions();
    actions.getChangeRequest();
  }

  componentDidUpdate(prevProps) {
    const { hasError } = this.props;
    if (hasError && !prevProps.hasError) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ showErrorPopup: true });
    }
  }

  getColumns = () => {
    const { account, company } = this.props;
    const columns = [
      {
        title: 'Date',
        key: 'txndate',
        dataIndex: 'txndate',
        render: (date) => (moment(date)
          .format('DD MMM YYYY, HH:mm')),
        minWidth: 100,
      },
      {
        title: 'Details',
        key: 'description',
        render: (_0, transaction) => this.getDetails(transaction),
        minWidth: 100,
      },
      {
        title: 'Reference',
        key: 'reference',
        render: (_0, transaction) => this.getReference(transaction),
        minWidth: 100,
      },
      {
        title: 'Type',
        key: 'type',
        dataIndex: 'type',
        render: (type, transaction) => this.getTypeText(type, transaction),
        minWidth: 100,
      },
      {
        title: 'Amount',
        key: 'nativeCurrency',
        dataIndex: 'nativeCurrency',
        render: (sum) => <div className={styles.Amount}>{amountFormatter(sum)}</div>,
        align: 'right',
        width: 100,
      },
      {
        title: 'Merchant',
        key: 'category',
        dataIndex: 'category',
        minWidth: 100,
      },
      {
        title: (
          <div>
            Category
            <Tooltip
              title="You can change the category from here, too. &quot;Personal expenses&quot; are automatically excluded from tax-related calculations."
              placement="top"
            >
              <Icon name="ic_info" className={styles.InfoIcon} />
            </Tooltip>
          </div>
        ),
        key: 'expense_type',
        dataIndex: 'expense_type',
        editable: true,
        render: (type, transaction) => this.renderExpenseCategoryCell(type, transaction),
        minWidth: 100,
      },
      {
        title: (
          <div>
            Attachments
            <Tooltip
              title="View attachments here, or press a button to receive an in-app notification to add the receipt/invoice later."
              placement="top"
            >
              <Icon name="ic_info" className={styles.InfoIcon} />
            </Tooltip>
          </div>
        ),
        key: 'attachments',
        dataIndex: 'attachments',
        render: (attachments, transaction) => this.renderAttachmentCell(attachments, transaction),
        minWidth: 100,
      },
    ];

    /* Hide category columnt for expenses */
    if (account && account.id.startsWith('expenses_')) {
      columns.splice(5, 1);
    }

    /* Add VAT columnt for pays_at company */
    if (company.pays_vat) {
      columns.splice(3, 0,
        {
          title: (
            <div className={styles.Amount}>
              VAT
              <Tooltip
                title="This data is taken from the receipts or invoices"
                placement="top"
              >
                <Icon name="ic_info" className={styles.InfoIcon} />
              </Tooltip>
            </div>
          ),
          key: 'vat',
          dataIndex: 'vat',
          render: (sum) => this.renderVatCell(sum),
          minWidth: 20,
        });
    }

    return columns;
  };

  getDetails = (transaction) => {
    const { description, note } = transaction;

    return (
      <>
        <div>{description}</div>
        {note ? (
          <Popover trigger="hover" type="dark" content={note}>
            <a>Note</a>
          </Popover>
        ) : null}
      </>
    );
  };

  getReference = (transaction) => {
    const reference = transaction.details?.transferDetails?.reference;

    return reference ? <div>{reference}</div> : null;
  }

  getTypeText = (type, transaction) => {
    if (type === 'manual') {
      return this.manualTitleTypes[transaction.title_type];
    }
    return this.paymentTypes[type];
  };

  renderVatCell = (vat) => {
    if (!vat) {
      return <span>&mdash;</span>;
    }
    return amountFormatter({ amount: vat.vat_amount, currency: 'GBP' });
  };

  request = (transaction, type) => {
    const { actions } = this.props;
    actions.sendChangeRequest(transaction.transaction_id, type);
  };

  renderExpenseCategoryCell = (expenseType, transaction) => {
    const { actions } = this.props;
    const { updateTransactionCategory } = actions;

    const direction = transaction.nativeCurrency.amount < 0 ? 'outcome' : 'income';

    return (
      <div className={styles.ExpenseCategoryCell}>
        <CategoryCell
          value={expenseType}
          direction={direction}
          onCategorySelected={({ categoryId, categoryName }) => {
            updateTransactionCategory(transaction.transaction_id, categoryId, categoryName);
            gaSendUserEvent(gaEventName.clickChangeCategory, categoryId);
          }}
        />
      </div>
    );
  };

  renderAttachmentCell = (attachments, transaction) => {
    let attachmentButton = null;
    if (attachments.length > 0) {
      attachmentButton = (
        <Button
          type="link"
          icon="ic_attachment"
          text={attachments.length === 1 ? 'Attachment' : `${attachments.length} attachments`}
          onClick={() => {
            this.showFiles(transaction);
            gaSendUserEvent(gaEventName.clickShowAttachments);
          }}
        />
      );
    }

    return (
      <div>
        {attachmentButton}
      </div>
    );
  };

  checkHasMore = (loading, transactionsList, totalTransactions) => (loading === false
    && transactionsList.length < totalTransactions);

  showFiles = (transaction) => {
    this.setState({
      showImagePopup: true,
      currentTransaction: transaction,
    });
  };

  renderImageViewer = () => {
    const { currentTransaction } = this.state;
    return (
      <div>
        <Image
          attachments={currentTransaction.attachments}
          onClose={() => {
            this.setState({
              showImagePopup: false,
              currentTransaction: null,
            });
          }}
        />
      </div>
    );
  };

  render() {
    const {
      loading,
      transactionsList,
      totalTransactions,
      actions,
    } = this.props;

    const { showImagePopup, showErrorPopup } = this.state;

    return (
      <div className={styles.TransactionsTable}>
        <div className={styles.Title}>Transactions</div>
        <Table
          rowKey={(record) => record.transaction_id}
          data={transactionsList}
          columns={this.getColumns()}
          loading={loading}
          onSort={this.onSort}
          loadMore={() => actions.getMoreTransactions()}
          hasMore={this.checkHasMore(loading, transactionsList, totalTransactions)}
        />
        {showImagePopup && this.renderImageViewer()}
        <Modal
          title="Something went wrong here"
          okText="Reload transactions"
          visible={showErrorPopup}
          onOk={() => {
            actions.getMoreTransactions();
            this.setState({ showErrorPopup: false });
          }}
          onCancel={() => this.setState({ showErrorPopup: false })}
          closable
        >
          <p>
            Please try to reload transactions or refresh the page
            and if it doesn&apos;t help – let us know via in-app chat.
          </p>
        </Modal>
      </div>
    );
  }
}

TransactionsTable.propTypes = {
  loading: PropTypes.bool.isRequired,
  hasError: PropTypes.bool.isRequired,
  transactionsList: PropTypes.instanceOf(Array).isRequired,
  totalTransactions: PropTypes.number.isRequired,
  changeRequests: PropTypes.shape({}).isRequired,
  account: PropTypes.shape({
    id: PropTypes.string,
  }),
  company: PropTypes.shape({
    pays_vat: PropTypes.bool,
  }),
  actions: PropTypes.shape({
    getMoreTransactions: PropTypes.func.isRequired,
    getChangeRequest: PropTypes.func.isRequired,
    sendChangeRequest: PropTypes.func.isRequired,
    updateTransactionCategory: PropTypes.func.isRequired,
  }).isRequired,
};

TransactionsTable.defaultProps = {
  company: null,
  account: null,
};

const mapStateToProps = ({ transactionsService, requestsService, accountService }) => ({
  company: accountService.company,
  account: accountService.account,
  loading: transactionsService.loading,
  hasError: Boolean(transactionsService.error),
  transactions: transactionsService.transactions,
  totalTransactions: transactionsService.totalTransactions,
  transactionsList: transactionsService.transactionsList,
  changeRequests: requestsService.changeRequests,
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators({
    getMoreTransactions: getMoreTransactionsActions.common,
    updateTransactionCategory: updateTransactionCategoryActions.common,
    getChangeRequest,
    sendChangeRequest,
    updateDates,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(TransactionsTable);
